import { usePointDistortion } from "./PointDistortionContext";
import { useMemo } from "react";
import {
    clientToSvgPoint,
    useSvgViewBoxZoom,
} from "../../hooks/useSvgViewBoxZoom";
import { useUndoRedoKeyboard } from "../../hooks/useUndoRedoKeyboard";

export function PointDistortionViewer() {
    const {
        svg,
        points,
        viewBoxAttr,
        distortedSvgContent,
        canExport,
        handleDownload,
        addPoint,
        undo,
        redo,
    } = usePointDistortion();

    useUndoRedoKeyboard({ undo, redo });

    // Calculate SVG dimensions for size conversion
    const svgDimensions = useMemo(() => {
        if (!svg) return null;
        const viewBoxParts = viewBoxAttr.split(/\s+/).map(Number);
        if (viewBoxParts.length !== 4) return null;
        const [, , width, height] = viewBoxParts;
        return { width, height };
    }, [svg, viewBoxAttr]);

    const base = useMemo(() => {
        const [x, y, w, h] = viewBoxAttr.split(/\s+/).map(Number);
        return { x, y, w, h };
    }, [viewBoxAttr]);

    const {
        svgRef,
        viewBoxAttr: viewBox,
        panHandlers,
        zoomPercent,
        zoomIn,
        zoomOut,
        resetView,
    } = useSvgViewBoxZoom({ base });

    const handleSvgClick = (event: React.MouseEvent<SVGSVGElement>) => {
        if (!svgRef.current) return;
        const { x, y } = clientToSvgPoint(
            svgRef.current,
            event.clientX,
            event.clientY,
        );
        addPoint(x, y);
    };

    const displayContent = distortedSvgContent || svg?.content || null;

    return (
        <div className="panel-viewer point-distortion-viewer">
            <div className="path-tool-preview">
                <div className="svg-zoom-controls">
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={zoomOut}
                        title="Zoom out"
                    >
                        −
                    </button>
                    <span className="zoom-level">{zoomPercent}%</span>
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={zoomIn}
                        title="Zoom in"
                    >
                        +
                    </button>
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={resetView}
                        title="Reset zoom"
                    >
                        ⌂
                    </button>
                </div>
                <div className="svg-scroll-wrapper">
                    <div className="svg-container svg-container--fill">
                        {displayContent ? (
                            <svg
                                ref={svgRef}
                                viewBox={viewBox}
                                xmlns="http://www.w3.org/2000/svg"
                                className="path-tool-svg"
                                onClick={handleSvgClick}
                                style={{ cursor: "crosshair", touchAction: "none" }}
                                {...panHandlers}
                            >
                                <g
                                    className="distorted-content"
                                    dangerouslySetInnerHTML={{
                                        __html: displayContent
                                            .replace(/<\/?svg[^>]*>/gi, "")
                                            .trim(),
                                    }}
                                />
                                {points.map((point, idx) => {
                                    // Convert percentage size to SVG units for display
                                    const displaySize = svgDimensions
                                        ? (point.size / 100) *
                                          Math.sqrt(
                                              svgDimensions.width *
                                                  svgDimensions.width +
                                                  svgDimensions.height *
                                                      svgDimensions.height,
                                          )
                                        : point.size;
                                    return (
                                        <g key={point.id}>
                                            <circle
                                                cx={point.x}
                                                cy={point.y}
                                                r={displaySize}
                                                fill="none"
                                                stroke="#ff6b6b"
                                                strokeWidth="2"
                                                strokeDasharray="5,5"
                                                opacity={0.5}
                                            />
                                            <circle
                                                cx={point.x}
                                                cy={point.y}
                                                r="5"
                                                fill="#ff6b6b"
                                                stroke="#fff"
                                                strokeWidth="2"
                                            />
                                            <text
                                                x={point.x}
                                                y={point.y - 10}
                                                fill="#ff6b6b"
                                                fontSize="12"
                                                fontWeight="bold"
                                                textAnchor="middle"
                                            >
                                                {idx + 1}
                                            </text>
                                        </g>
                                    );
                                })}
                            </svg>
                        ) : (
                            <div className="svg-placeholder">
                                <p>Import an SVG file to get started</p>
                                <p className="svg-placeholder-hint">
                                    Click on the SVG to add distortion points
                                </p>
                            </div>
                        )}
                    </div>
                </div>
                <div className="path-tool-footer">
                    <p className="path-tool-meta">
                        {svg
                            ? points.length > 0
                                ? `${points.length} distortion point${
                                      points.length === 1 ? "" : "s"
                                  } defined.`
                                : "Click on the SVG to add distortion points."
                            : "Waiting for SVG file."}
                    </p>
                    <button
                        type="button"
                        className="generic-button"
                        disabled={!canExport}
                        onClick={handleDownload}
                    >
                        Download result SVG
                    </button>
                </div>
            </div>
        </div>
    );
}
